import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createLogger } from '../../utils/logger';
import { apiFetch } from '../../utils/api';
import { mobileFetch } from '../api';

const log = createLogger('MobileGeneratePage');

interface SubmissionResult {
  scan_id: string;
  job_id?: string;
  status: 'pending' | 'submitted' | 'error';
  error?: string;
}

// Header names that identify the scan-id column in an uploaded CSV.
const SCAN_ID_HEADERS = ['scan_id', 'scanid', 'id', 'scan', 'inspection_id', 'inspectionid'];

type LicensePlateOption = 'no_plate' | 'bracket_only' | 'keep_plate';

const LP_OPTIONS: { value: LicensePlateOption; label: string }[] = [
  { value: 'no_plate', label: 'No plate' },
  { value: 'bracket_only', label: 'Bracket only' },
  { value: 'keep_plate', label: 'Keep plate' },
];

// The knobs mobile's prompt understands. Phone captures need one that merch
// does not: they arrive dirty and damaged far more often than photobooth
// shots, so the operator decides whether to recondition — wash and repair
// together, since a polished car still caked in mud sells nothing.
interface MobileScanSettings {
  lpFront: LicensePlateOption;
  lpRear: LicensePlateOption;
  metadataFree: boolean;
  highReflectance: boolean;
  recondition: boolean;
}

const SETTINGS_FALLBACK: MobileScanSettings = {
  lpFront: 'no_plate',
  lpRear: 'no_plate',
  metadataFree: true,
  highReflectance: true,
  recondition: true,
};

function settingsPayload(settings: MobileScanSettings) {
  return {
    license_plate: { front: settings.lpFront, rear: settings.lpRear },
    metadata_free: settings.metadataFree,
    high_reflectance: settings.highReflectance,
    recondition: settings.recondition,
  };
}

/** Prompt knobs rendered identically for a single scan and a batch. */
function ScanSettingsControls({
  idPrefix,
  settings,
  onChange,
}: {
  idPrefix: string;
  settings: MobileScanSettings;
  onChange: (next: MobileScanSettings) => void;
}) {
  const selectClass =
    'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200';

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${idPrefix}LpFront`} className="block text-xs font-medium text-slate-600 mb-1">
            Plate (Front)
          </label>
          <select
            id={`${idPrefix}LpFront`}
            value={settings.lpFront}
            onChange={(e) => onChange({ ...settings, lpFront: e.target.value as LicensePlateOption })}
            className={selectClass}
          >
            {LP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${idPrefix}LpRear`} className="block text-xs font-medium text-slate-600 mb-1">
            Plate (Rear)
          </label>
          <select
            id={`${idPrefix}LpRear`}
            value={settings.lpRear}
            onChange={(e) => onChange({ ...settings, lpRear: e.target.value as LicensePlateOption })}
            className={selectClass}
          >
            {LP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <label htmlFor={`${idPrefix}MetadataFree`} className="flex items-center gap-2 cursor-pointer select-none">
        <input
          id={`${idPrefix}MetadataFree`}
          type="checkbox"
          checked={settings.metadataFree}
          onChange={(e) => onChange({ ...settings, metadataFree: e.target.checked })}
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-xs font-medium text-slate-600">
          Metadata free (rely on the source photos, not the vehicle record)
        </span>
      </label>

      <label htmlFor={`${idPrefix}HighReflectance`} className="flex items-center gap-2 cursor-pointer select-none">
        <input
          id={`${idPrefix}HighReflectance`}
          type="checkbox"
          checked={settings.highReflectance}
          onChange={(e) => onChange({ ...settings, highReflectance: e.target.checked })}
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-xs font-medium text-slate-600">
          High reflectance (add stronger outdoor-style reflections)
        </span>
      </label>

      <label htmlFor={`${idPrefix}Recondition`} className="flex items-center gap-2 cursor-pointer select-none">
        <input
          id={`${idPrefix}Recondition`}
          type="checkbox"
          checked={settings.recondition}
          onChange={(e) => onChange({ ...settings, recondition: e.target.checked })}
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-xs font-medium text-slate-600">
          Recondition (remove dirt, dust and rain marks, and repair visible damage)
        </span>
      </label>
    </>
  );
}

export function MobileGeneratePage() {
  // Single scan state
  const [singleScanId, setSingleScanId] = useState('');
  const [singleSubmitting, setSingleSubmitting] = useState(false);
  const [singleResult, setSingleResult] = useState<SubmissionResult | null>(null);
  const [backgroundName, setBackgroundName] = useState('');
  const [singleSettings, setSingleSettings] = useState<MobileScanSettings>(SETTINGS_FALLBACK);

  // Batch upload state
  const [batchFileName, setBatchFileName] = useState<string | null>(null);
  const [batchScanIds, setBatchScanIds] = useState<string[]>([]);
  const [batchResults, setBatchResults] = useState<SubmissionResult[]>([]);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchBackgroundName, setBatchBackgroundName] = useState('');
  const [batchSettings, setBatchSettings] = useState<MobileScanSettings>(SETTINGS_FALLBACK);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Shared background list. Mobile and merch draw from the same bank, which
  // lives on the merch side, so this one call is deliberately aimed at
  // lightning rather than at `/api/mobile`.
  const [backgroundNames, setBackgroundNames] = useState<string[]>([]);

  useEffect(() => {
    apiFetch('/api/settings/photobooths', { app: 'lightning' })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setBackgroundNames(
            Array.isArray(data) ? data.map((p: { name: string }) => p.name) : []
          );
        }
      })
      .catch((err) => log.error('Failed to fetch backgrounds:', err));

    // Seed the controls from the backend so the form never disagrees with
    // what an unset scan would actually be generated with.
    mobileFetch('/api/settings/scan-defaults')
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        const defaults: MobileScanSettings = {
          lpFront: (data?.license_plate?.front ?? SETTINGS_FALLBACK.lpFront) as LicensePlateOption,
          lpRear: (data?.license_plate?.rear ?? SETTINGS_FALLBACK.lpRear) as LicensePlateOption,
          metadataFree: Boolean(data?.metadata_free ?? SETTINGS_FALLBACK.metadataFree),
          highReflectance: Boolean(data?.high_reflectance ?? SETTINGS_FALLBACK.highReflectance),
          recondition: Boolean(data?.recondition ?? SETTINGS_FALLBACK.recondition),
        };
        setSingleSettings(defaults);
        setBatchSettings(defaults);
      })
      .catch((err) => log.error('Failed to fetch scan defaults:', err));
  }, []);

  // Submit a single scan
  const submitSingleScan = async () => {
    if (!singleScanId.trim()) return;

    setSingleSubmitting(true);
    setSingleResult(null);

    try {
      const payload: Record<string, unknown> = {
        scan_id: singleScanId.trim(),
        source: 'manual',
      };
      if (backgroundName) payload.background_name = backgroundName;
      payload.settings = settingsPayload(singleSettings);

      const response = await mobileFetch('/scans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        log.info(`Single scan submitted: scan_id=${singleScanId.trim()}, job_id=${data.job_id}`);
        setSingleResult({
          scan_id: singleScanId.trim(),
          job_id: data.job_id,
          status: 'submitted',
        });
        setSingleScanId('');
      } else {
        const error = await response.text();
        log.error(`Single scan submission failed: scan_id=${singleScanId.trim()}, status=${response.status}`, error);
        setSingleResult({
          scan_id: singleScanId.trim(),
          status: 'error',
          error: error || 'Failed to submit scan',
        });
      }
    } catch (err) {
      log.error('Single scan submission network error:', err);
      setSingleResult({
        scan_id: singleScanId.trim(),
        status: 'error',
        error: err instanceof Error ? err.message : 'Network error',
      });
    } finally {
      setSingleSubmitting(false);
    }
  };

  // Parse CSV file. Only the scan-id column is read; a header row is optional
  // and files without one fall back to first-column-only.
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBatchFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      const splitRow = (line: string) => line.split(',').map((part) => part.trim());
      const isValidScanId = (id: string) =>
        /^[a-zA-Z0-9-_]+$/.test(id) && id.length > 5 && !SCAN_ID_HEADERS.includes(id.toLowerCase());

      const lines = text.split(/[\r\n]+/).filter((line) => line.trim());
      if (lines.length === 0) {
        setBatchScanIds([]);
        setBatchResults([]);
        return;
      }

      const header = splitRow(lines[0]).map((h) => h.toLowerCase());
      let scanCol = header.findIndex((h) => SCAN_ID_HEADERS.includes(h));
      const hasHeader = scanCol >= 0;
      if (scanCol < 0) scanCol = 0;
      const dataLines = hasHeader ? lines.slice(1) : lines;

      const validIds: string[] = [];
      for (const line of dataLines) {
        const id = splitRow(line)[scanCol];
        if (!id || !isValidScanId(id)) continue;
        validIds.push(id);
      }

      setBatchScanIds(validIds);
      setBatchResults([]);
    };

    reader.readAsText(file);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Submit batch scans via batch API
  const submitBatchScans = async () => {
    if (batchScanIds.length === 0 || !batchFileName) return;

    setBatchSubmitting(true);
    setBatchProgress({ current: 0, total: batchScanIds.length });

    try {
      const batchPayload: Record<string, unknown> = {
        filename: batchFileName,
        scan_ids: batchScanIds,
      };
      if (batchBackgroundName) batchPayload.background = batchBackgroundName;
      batchPayload.settings = settingsPayload(batchSettings);

      const response = await mobileFetch('/api/batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batchPayload),
      });

      if (response.ok) {
        const data = await response.json();
        log.info(`Batch submitted: batch_id=${data.batch_id}, scans=${batchScanIds.length}, filename=${batchFileName}`);
        // Mark all as submitted
        const results: SubmissionResult[] = batchScanIds.map(scanId => ({
          scan_id: scanId,
          job_id: data.batch_id,
          status: 'submitted' as const,
        }));
        setBatchResults(results);
        setBatchProgress({ current: batchScanIds.length, total: batchScanIds.length });
      } else {
        const error = await response.text();
        log.error(`Batch submission failed: status=${response.status}, scans=${batchScanIds.length}`, error);
        // Mark all as error
        const results: SubmissionResult[] = batchScanIds.map(scanId => ({
          scan_id: scanId,
          status: 'error' as const,
          error: error || 'Failed to submit batch',
        }));
        setBatchResults(results);
      }
    } catch (err) {
      log.error('Batch submission network error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      const results: SubmissionResult[] = batchScanIds.map(scanId => ({
        scan_id: scanId,
        status: 'error' as const,
        error: errorMsg,
      }));
      setBatchResults(results);
    } finally {
      setBatchSubmitting(false);
    }
  };

  // Clear batch
  const clearBatch = () => {
    setBatchFileName(null);
    setBatchScanIds([]);
    setBatchResults([]);
    setBatchProgress({ current: 0, total: 0 });
  };

  const successCount = batchResults.filter(r => r.status === 'submitted').length;
  const errorCount = batchResults.filter(r => r.status === 'error').length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Single Scan Section */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Single Scan</h2>
            <p className="text-sm text-slate-500">Generate images for one scan ID</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="scanId" className="block text-sm font-medium text-slate-700 mb-2">
              Scan ID
            </label>
            <input
              id="scanId"
              type="text"
              value={singleScanId}
              onChange={(e) => setSingleScanId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSingleScan()}
              placeholder="e.g., 57942aad-a26d-4b05-8c1b-7de5aa32326d"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 font-mono text-sm transition-all duration-200"
            />
          </div>

          <div>
            <label htmlFor="background" className="block text-xs font-medium text-slate-600 mb-1">Background</label>
            <select
              id="background"
              value={backgroundName}
              onChange={(e) => setBackgroundName(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
            >
              <option value="">Server default</option>
              {backgroundNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <ScanSettingsControls
            idPrefix="single"
            settings={singleSettings}
            onChange={setSingleSettings}
          />

          <button
            onClick={submitSingleScan}
            disabled={!singleScanId.trim() || singleSubmitting}
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {singleSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate
              </>
            )}
          </button>

          {/* Single scan result */}
          {singleResult && (
            <div className={`p-4 rounded-lg ${
              singleResult.status === 'submitted'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}>
              {singleResult.status === 'submitted' ? (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Scan submitted successfully</p>
                    <p className="text-xs font-mono mt-1 text-slate-600">Job ID: {singleResult.job_id}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Failed to submit scan</p>
                    <p className="text-xs text-slate-600 mt-1">{singleResult.error}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Batch Upload Section */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Batch Upload</h2>
            <p className="text-sm text-slate-500">Generate images from a CSV file</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* File upload area */}
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all duration-200">
              <svg className="w-12 h-12 mx-auto text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-slate-700">
                Drop a CSV file here or <span className="text-indigo-600 font-medium">browse</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                One scan ID per line or comma-separated
              </p>
            </div>
          </div>

          {/* Scan IDs preview */}
          {batchScanIds.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">
                    <span className="text-slate-900 font-semibold">{batchScanIds.length}</span> scan IDs loaded
                  </span>
                  {batchFileName && (
                    <span className="text-xs text-slate-400 font-mono">({batchFileName})</span>
                  )}
                </div>
                <button
                  onClick={clearBatch}
                  className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {batchScanIds.slice(0, 10).map((id, idx) => (
                  <div key={idx} className="text-xs font-mono text-slate-500 truncate">
                    {id}
                  </div>
                ))}
                {batchScanIds.length > 10 && (
                  <div className="text-xs text-slate-400">
                    ... and {batchScanIds.length - 10} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Batch settings */}
          {batchScanIds.length > 0 && (
            <div className="space-y-4">
              <div>
                <label htmlFor="batchBackground" className="block text-xs font-medium text-slate-600 mb-1">Background</label>
                <select
                  id="batchBackground"
                  value={batchBackgroundName}
                  onChange={(e) => setBatchBackgroundName(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  <option value="">Server default</option>
                  {backgroundNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              <ScanSettingsControls
                idPrefix="batch"
                settings={batchSettings}
                onChange={setBatchSettings}
              />
            </div>
          )}

          {/* Submit batch button */}
          <button
            onClick={submitBatchScans}
            disabled={batchScanIds.length === 0 || batchSubmitting}
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {batchSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting {batchProgress.current} / {batchProgress.total}...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Submit Batch ({batchScanIds.length} scans)
              </>
            )}
          </button>

          {/* Batch results summary */}
          {batchResults.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-900">Submission Results</span>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-green-600">{successCount} submitted</span>
                  {errorCount > 0 && <span className="text-red-600">{errorCount} failed</span>}
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-200 rounded-full h-2 mb-3 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                  style={{ width: `${(successCount / batchResults.length) * 100}%` }}
                />
              </div>

              {/* Show errors if any */}
              {errorCount > 0 && (
                <div className="max-h-24 overflow-y-auto space-y-1">
                  {batchResults
                    .filter(r => r.status === 'error')
                    .slice(0, 5)
                    .map((result, idx) => (
                      <div key={idx} className="text-xs text-red-600 truncate">
                        {result.scan_id}: {result.error}
                      </div>
                    ))}
                </div>
              )}

              <Link
                to="/mobile/live"
                className="mt-4 block w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-center text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                View Progress in Live Scans
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
